import { equals, find } from "ramda";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { Key } from "react-stately";
import { clearFields } from "redux-form";
import { useDebounce } from "use-debounce";
import { ComboBox, type ComboBoxProps, HStack, Icons, Item, MenuOptionItem, SymbolIcon, TriggerButton } from "widgets";

import type { Capability, Organism } from "@/graphql.ts";
import {
  type GenomeComboBoxDataFragment,
  useGetGenomeLazyQuery,
  useSearchSequencedGenomeLazyQuery,
} from "@/modules/entities/graphql/genome-queries.gql";
import { checkCapability } from "@/modules/users/utils/user.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { CAPABILITY } from "@/settings/settings.ts";
import { VirtualGenomeCreationDialogForm } from "@/ui-modules/entities/genomes/VirtualGenomeCreationDialog.tsx";
import {
  VIRTUAL_GENOME_CREATION_FORM_KEYS,
  VIRTUAL_GENOME_CREATION_FORM_NAME,
} from "@/ui-modules/entities/genomes/VirtualGenomeCreationDialogSettings.ts";

type GenomeComboBoxProps = Omit<
  ComboBoxProps<GenomeComboBoxDataFragment>,
  "items" | "children" | "onSelectionChange"
> & {
  onSelectionChange?: (key: Key | null) => void;
  onGenomeChange?: (genome: GenomeComboBoxDataFragment | null) => void;
};

export const GenomeComboBox = (props: GenomeComboBoxProps) => {
  const dispatch = useDispatch();

  const [search] = useSearchSequencedGenomeLazyQuery();
  const [getGenome] = useGetGenomeLazyQuery();
  const userCapabilities = useSelector<IAppState, Capability[] | null>(
    (state) => state.auth.session?.capabilities?.filter((c) => c != null) ?? null,
    equals,
  );

  const [isLoading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [debouncedFilterText] = useDebounce(filterText, 300);
  const [items, setItems] = useState<GenomeComboBoxDataFragment[]>([]);

  const load = useCallback(
    async (searchTerm = "") => {
      setLoading(true);
      const res = await search({ variables: { searchTerm, limit: 50 } });
      // When using `network-only` fetch policy, it looks like we get a frozen array.
      const items = res.data?.search.sequencedGenomes.genomes ?? [];
      const organismId = props.selectedKey ? Number(props.selectedKey) : null;

      // Load the selected genome if not in the list (first load usually)
      if (organismId && !items.some((genome) => genome.id === organismId)) {
        // if the initial genome is restricted it has a negative id, we don't ignore it for information and validation purpose
        if (organismId < 0) {
          items.unshift({
            id: organismId,
            name: "[Restricted Genome]",
            species: null,
          });
        } else {
          const res = await getGenome({ variables: { organismId } });
          if (res.data?.organism) {
            items.unshift(res.data.organism);
          }
        }
      }

      if (checkCapability(userCapabilities, CAPABILITY.CREATE_VIRTUAL_GENOME)) {
        items.unshift(addNewGenomeItem);
      }

      setItems(items);
      setLoading(false);
    },
    [getGenome, search, userCapabilities, props.selectedKey],
  );

  useEffect(() => {
    // Ignore the returned value
    void load(debouncedFilterText);
  }, [debouncedFilterText, load]);

  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const selectionChanged = useCallback(
    (key: Key | null): void => {
      // This should not be null regarding types, but it happens! (mostly on reload)
      if (key == null || Number(key) < 0) return;
      if (String(key) === String(addNewGenomeItem.id)) {
        setShowCreateDialog(true);
        return;
      }
      if (Number(key) === props.selectedKey) {
        return;
      }
      const selectedItem = find((item) => item.id === Number(key), items);
      props.onGenomeChange?.(selectedItem ?? null);
      props.onSelectionChange?.(Number(key));
    },
    [items, props],
  );

  return (
    <HStack>
      <ComboBox
        placeholder="A Genome Name"
        {...props}
        selectedKey={props.selectedKey ? String(props.selectedKey) : null}
        items={items}
        onInputChange={(filterText) => {
          setFilterText(filterText);
          props.onInputChange?.(filterText);
        }}
        onSelectionChange={selectionChanged}
        width={props.width ?? 180}
        isLoading={isLoading}
        isDisabled={props.isDisabled}
        icon={<img src={Icons.Tiles.Entity} width={20} height={20} alt="" />}
        style={{ flex: 1 }}
        action={
          props.selectedKey ? (
            <TriggerButton
              icon={<SymbolIcon icon="cancel" filled size={16} style={{ color: "var(--form-placeholder)" }} />}
              label=""
              aria-label="Delete"
              onPress={() => {
                props.onSelectionChange?.(null);
              }}
            />
          ) : undefined
        }
      >
        {(item) => (
          <Item key={String(item.id)} textValue={item.name ?? undefined}>
            {item.id === addNewGenomeItem.id ? (
              <MenuOptionItem title="New Virtual Genome" icon="add" />
            ) : (
              <MenuOptionItem
                title={item.name}
                description={item.species?.name}
                icon={<img src={Icons.Tiles.Entity} width={20} height={20} alt="" />}
              />
            )}
          </Item>
        )}
      </ComboBox>
      <VirtualGenomeCreationDialogForm
        isOpen={showCreateDialog}
        onClose={(genome: Organism) => {
          if (genome) {
            props.onSelectionChange?.(genome.id);
          }
          dispatch(
            clearFields(
              VIRTUAL_GENOME_CREATION_FORM_NAME,
              false,
              false,
              ...Object.values(VIRTUAL_GENOME_CREATION_FORM_KEYS),
            ),
          );
          setShowCreateDialog(false);
        }}
      />
    </HStack>
  );
};

const addNewGenomeItem = {
  id: "add-genome",
} as unknown as GenomeComboBoxDataFragment;
